// PalletClassificationPage.jsx (frontend local implementation)
// Copied from root src/pages with imports adjusted to stay within frontend src tree.
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { AuthProvider, useAuth } from '../context/AuthContext';
import AuthPanel from '../components/auth/AuthPanel';
import { uploadPalletImage, classifyImage, fetchHistoryPageAdapter, deleteClassificationRecord } from '../lib/palletApi';
import { SUPABASE_ENABLED } from '../lib/supabaseClient';
import UploadDropzone from '../components/pallets/UploadDropzone';
import ResultPanel from '../components/pallets/ResultPanel';
import HistoryTable from '../components/pallets/HistoryTable';
import DashboardHeader from '../components/DashboardHeader';

export const CONFIG = {
	endpoints: { classify: '/api/openai/classify', upload: '/api/upload', pallets: '/api/pallets' },
	limits: { maxFileMB: 10 },
	paging: { pageSize: 10 },
};

export function formatConfidence(conf) { if (conf == null || isNaN(conf)) return '—'; return `${Math.round(conf * 100)}%`; }

const ToastContext = React.createContext(null);
function ToastProvider({ children }) {
	const [toasts, setToasts] = useState([]);
	const push = (toast) => {
		const id = Math.random().toString(36).slice(2);
		setToasts(ts => [...ts, { id, ...toast }]);
		setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), toast.duration || 3200);
	};
	return (
		<ToastContext.Provider value={{ toast: push }}>
			{children}
			<div className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 flex w-auto sm:w-72 max-w-full flex-col gap-2">
				{toasts.map(t => (
					<div key={t.id} className="rounded-md border bg-surface/95 p-3 text-sm shadow-lg">
						{t.title && <p className="font-medium">{t.title}</p>}
						{t.description && <p className="mt-0.5 text-xs text-secondary">{t.description}</p>}
					</div>
				))}
			</div>
		</ToastContext.Provider>
	);
}
export function useToast() { return React.useContext(ToastContext); }

// Supabase-backed flows now handled via palletApi utilities.

const cn = (...c) => c.filter(Boolean).join(' ');
const Card = ({ className='', ...rest }) => <div className={cn('rounded-2xl border bg-elevated/30 p-6 shadow-sm', className)} {...rest} />;
const Button = ({ className='', variant='outline', disabled, ...rest }) => (
	<button
		{...rest}
		disabled={disabled}
		className={cn(
			'inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
			'border-default/50 bg-surface hover:bg-elevated/60 disabled:opacity-50 disabled:cursor-not-allowed',
			variant === 'primary' && 'bg-[var(--lodia-brand,#2563eb)] text-white hover:opacity-90 border-black/20 shadow-md',
			className
		)}
	/>
);
const Input = (props) => <input {...props} className={cn('w-full rounded-md border bg-surface px-3 py-2 text-sm outline-none focus:ring-2', props.className)} />;
const Progress = ({ value }) => <div className="h-1.5 w-full rounded-full bg-black/10" aria-label="progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={value}><div className="h-full rounded-full bg-[color:var(--lodia-brand)] transition-all" style={{ width: `${value}%` }} /></div>;

function PalletClassificationInner() {
	const { user, initializing, signOut } = useAuth() || {};
	const { toast } = useToast();
	const [file, setFile] = useState(null);
	const [name, setName] = useState('');
	const [classifying, setClassifying] = useState(false);
	const [classification, setClassification] = useState(null);
	const [error, setError] = useState('');
	const [saving, setSaving] = useState(false);
	const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
	const [palletImageId, setPalletImageId] = useState(null);
	// When the user selects a new file, ensure we don't reuse the previous uploaded image ID
	useEffect(() => {
		// Clear the previously uploaded image reference so onClassify uploads the new file
		setPalletImageId(null);
		// Also clear any previous classification result to avoid confusion
		if (classification) setClassification(null);
		// Clear any previous error
		if (error) setError('');
		// Optionally reset name if it was derived from previous result; leave as-is if user typed it
		// setName('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [file]);
	const canClassify = file && name && !classifying;
	const canSave = classification && !saving;
	const abortRef = useRef(null);
	const onClassify = async () => {
		if (!canClassify) return;
		setError('');
		setClassifying(true);
		setClassification(null);
		abortRef.current = new AbortController();
		try {
			// 1. Upload image (only once per session per file)
			let imgId = palletImageId;
			if (!imgId) {
				const meta = await uploadPalletImage(file, user);
				imgId = meta.id;
				setPalletImageId(imgId);
			}
			const started = performance.now();
			const classificationRow = await classifyImage(imgId); // returns DB row
			const elapsed = Math.round(performance.now() - started);
			const adapted = {
				label: classificationRow.predicted_label,
				confidence: classificationRow.predicted_confidence,
				model: classificationRow.model_version,
				raw: classificationRow.full_json,
				inference_ms: classificationRow.inference_ms || elapsed,
			};
			setClassification(adapted);
			toast({ title: 'Classification complete' });
			setHistoryRefreshKey(k => k + 1); // refresh history automatically
		} catch (e) {
			setError(e.message || 'Failed to classify');
			toast({ title: 'Error', description: e.message || 'Classification failed' });
		} finally {
			setClassifying(false);
		}
	};
	const onSave = async () => {
		// For Supabase-backed design, classification is already stored; repurpose as 'Reset'.
		setFile(null); setName(''); setClassification(null); setPalletImageId(null);
		toast({ title: 'Reset', description: 'Form cleared.' });
	};
	const fetchHistoryPage = useCallback(async (params) => fetchHistoryPageAdapter(params), []);
	const handleDelete = useCallback(async (id) => { await deleteClassificationRecord(id); toast({ title: 'Deleted' }); setHistoryRefreshKey(k => k + 1); }, [toast]);
	if (initializing) {
		return <div className="p-8 text-sm text-secondary">Loading session...</div>;
	}
	if (!user) {
		return (
			<div className="mx-auto w-full max-w-7xl px-4 py-8">
				{!SUPABASE_ENABLED && (
					<div className="mb-6 rounded-md border border-amber-300 bg-amber-50 p-4 text-xs text-amber-800" role="alert">
						<strong className="font-semibold">Supabase not configured.</strong> Running in temporary in-memory mode. Add environment variables <code>REACT_APP_SUPABASE_URL</code> and <code>REACT_APP_SUPABASE_ANON_KEY</code> then restart to persist data.
					</div>
				)}
				<div className="mb-8 flex flex-col gap-2">
					<nav className="text-xs text-secondary" aria-label="Breadcrumb">Lodia / Classification</nav>
					<h1 className="text-xl font-semibold tracking-tight">AI Pallet Classification</h1>
					<p className="text-sm text-secondary">Sign in to upload pallet images and run AI classification.</p>
				</div>
				<AuthPanel />
			</div>
		);
	}

	return (
		<>
			<DashboardHeader
				title="AI Pallet Classification"
				subtitle="Upload a pallet image and classify it using AI. Save and review history below."
				user={user}
				onLogout={signOut}
				showSidebarTrigger={true}
			/>
			<div className="mx-auto w-full max-w-7xl px-4 py-8">
				{!SUPABASE_ENABLED && (
					<div className="mb-6 rounded-md border border-amber-300 bg-amber-50 p-4 text-xs text-amber-800" role="alert">
						<strong className="font-semibold">Supabase not configured.</strong> Running in temporary in-memory mode. Add environment variables <code>REACT_APP_SUPABASE_URL</code> and <code>REACT_APP_SUPABASE_ANON_KEY</code> then restart to persist data.
					</div>
				)}
			<div className="grid gap-6 md:grid-cols-2">
				<Card>
					<h2 className="text-lg font-semibold">Upload & Classify</h2>
					<p className="mt-1 text-xs text-secondary/90">Provide an image and pallet name to run inference.</p>
					<div className="mt-5 space-y-5">
						<div>
							<label className="mb-2 block text-xs font-medium">Pallet Name / ID<span className="text-red-500"> *</span></label>
							<Input value={name} aria-required="true" onChange={(e) => setName(e.target.value)} placeholder="e.g. Pallet A12" />
						</div>
						<div>
							<label className="mb-2 block text-xs font-medium">Image<span className="text-red-500"> *</span></label>
							<UploadDropzone value={file} onChange={setFile} disabled={classifying} maxFileMB={CONFIG.limits.maxFileMB} />
						</div>
						{error && <p className="text-xs text-red-500" role="alert">{error}</p>}
						<div className="flex items-center gap-3">
							<Button data-testid="classify-button" variant="primary" disabled={!canClassify} onClick={onClassify} aria-disabled={!canClassify} aria-busy={classifying}>{classifying ? 'Classifying...' : 'Classify'}</Button>
							{classifying && (
								<div className="flex items-center gap-2" aria-live="polite">
									<span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-black/30 border-t-black" aria-hidden="true"></span>
									<span className="text-xs text-black">Analyserar bilden…</span>
								</div>
							)}
						</div>
						{classification && (
							<div className="rounded-lg border bg-surface p-3 text-xs">
								<div className="flex flex-wrap gap-4">
									<span><span className="text-secondary/90">Label:</span> {classification.label}</span>
									<span><span className="text-secondary/90">Confidence:</span> {formatConfidence(classification.confidence)}</span>
									{/* Model hidden from users for now */}
								</div>
							</div>
						)}
					</div>
				</Card>
					<ResultPanel result={classification} raw={classification?.raw} onSave={onSave} saving={false} canSave={true} formatConfidence={formatConfidence} />
			</div>
			<HistoryTable fetchPage={fetchHistoryPage} refreshSignal={historyRefreshKey} formatConfidence={formatConfidence} onDelete={handleDelete} initialPageSize={CONFIG.paging.pageSize} />
		</div>
	</>
	);
}

export default function PalletClassificationPage() {
	return (
		<AuthProvider>
			<ToastProvider>
				<PalletClassificationInner />
			</ToastProvider>
		</AuthProvider>
	);
}

